'use client'

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react'

export interface VehicleMismatchData {
  registrationNumber: string
  context: 'bike' | 'car' | any
  message: string
}

export interface IllogicalFlowData {
  error_code: string
  message: string
  user_logged_in?: boolean
  last_digits?: string
  redirect_url?: string
  registration_number?: string
  vehicle_type?: string
  [key: string]: any
}

export interface ProposalData {
  proposal_id?: string
  registration_number?: string
  vehicle_details?: Record<string, any>
  pricing?: Record<string, any>
  [key: string]: any
}

interface GICarContextType {
  isLoading: boolean
  setIsLoading: (loading: boolean) => void

  proposalData: ProposalData | null
  setProposalData: (data: ProposalData) => void

  vehicleMismatchData: VehicleMismatchData | null
  setVehicleMismatchData: (data: VehicleMismatchData | null) => void

  illogicalFlowData: IllogicalFlowData | null
  setIllogicalFlowData: (data: IllogicalFlowData | null) => void

  registrationNumber: string
  setRegistrationNumber: (value: string) => void

  errorMessage: string
  setErrorMessage: (message: string) => void

  resetProposalData: () => void
  resetErrors: () => void
  resetAll: () => void
}

const GICarContext = createContext<GICarContextType | undefined>(undefined)

export const GICarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [proposalData, setProposalData] = useState<ProposalData | null>(null)
  const [vehicleMismatchData, setVehicleMismatchData] = useState<VehicleMismatchData | null>(null)
  const [illogicalFlowData, setIllogicalFlowData] = useState<IllogicalFlowData | null>(null)
  const [registrationNumber, setRegistrationNumber] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const resetProposalData = useCallback(() => {
    setProposalData(null)
  }, [])

  const resetErrors = useCallback(() => {
    setVehicleMismatchData(null)
    setIllogicalFlowData(null)
    setErrorMessage('')
  }, [])

  const resetAll = useCallback(() => {
    setIsLoading(false)
    setProposalData(null)
    setVehicleMismatchData(null)
    setIllogicalFlowData(null)
    setRegistrationNumber('')
    setErrorMessage('')
  }, [])

  const value: GICarContextType = {
    isLoading,
    setIsLoading,
    proposalData,
    setProposalData,
    vehicleMismatchData,
    setVehicleMismatchData,
    illogicalFlowData,
    setIllogicalFlowData,
    registrationNumber,
    setRegistrationNumber,
    errorMessage,
    setErrorMessage,
    resetProposalData,
    resetErrors,
    resetAll,
  }

  return <GICarContext.Provider value={value}>{children}</GICarContext.Provider>
}

export const useGICar = (): GICarContextType => {
  const context = useContext(GICarContext)
  if (context === undefined) {
    throw new Error('useGICar must be used within GICarProvider')
  }
  return context
}
